import AdminTableFiltersCard from "../ui/AdminTableFiltersCard";
import type { AdminGigIssueScope } from "../../../types/adminTypes";

type AdminIssuesFiltersProps = {
  search: string;
  status: string;
  issueScope: "" | AdminGigIssueScope;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onIssueScopeChange: (value: "" | AdminGigIssueScope) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
};

const AdminIssuesFilters = ({
  search,
  status,
  issueScope,
  createdFromDate,
  createdToDate,
  pageSize,
  onSearchChange,
  onStatusChange,
  onIssueScopeChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
}: AdminIssuesFiltersProps) => {
  return (
    <AdminTableFiltersCard>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[320px] flex-[2_1_360px]">
          <label htmlFor="admin-issues-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Search
          </label>
          <input
            id="admin-issues-filter-search"
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Gig title, teen, adult, issue text"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-issues-filter-status" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Status
          </label>
          <select
            id="admin-issues-filter-status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All statuses</option>
            <option value="OPEN">OPEN</option>
            <option value="UNDER_REVIEW">UNDER_REVIEW</option>
            <option value="AWAITING_SETTLEMENT_APPROVAL">AWAITING_SETTLEMENT_APPROVAL</option>
            <option value="RESOLVED_RELEASE_PAYOUT">RESOLVED_RELEASE_PAYOUT</option>
            <option value="RESOLVED_SETTLEMENT">RESOLVED_SETTLEMENT</option>
            <option value="RESOLVED_REFUND_ADULT">RESOLVED_REFUND_ADULT</option>
            <option value="RESOLVED_REFUND_ADULT_WITH_LATE_FEE">RESOLVED_REFUND_ADULT_WITH_LATE_FEE</option>
            <option value="RESOLVED_PREPARATION_PAYOUT_AND_REFUND">RESOLVED_PREPARATION_PAYOUT_AND_REFUND</option>
            <option value="RESOLVED_TEEN_REMOVED_KEEP_FUNDED">RESOLVED_TEEN_REMOVED_KEEP_FUNDED</option>
          </select>
        </div>
        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-issues-filter-scope" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Scope
          </label>
          <select
            id="admin-issues-filter-scope"
            value={issueScope}
            onChange={(event) => onIssueScopeChange(event.target.value as "" | AdminGigIssueScope)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All scopes</option>
            <option value="TEEN_ASSIGNMENT">Teen assignment</option>
            <option value="WHOLE_GIG">Whole gig</option>
          </select>
        </div>
        <div className="min-w-[150px] flex-[0_1_170px]">
          <label htmlFor="admin-issues-filter-created-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created From
          </label>
          <input
            id="admin-issues-filter-created-from"
            type="date"
            value={createdFromDate}
            onChange={(event) => onCreatedFromDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[150px] flex-[0_1_170px]">
          <label htmlFor="admin-issues-filter-created-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created To
          </label>
          <input
            id="admin-issues-filter-created-to"
            type="date"
            min={createdFromDate || undefined}
            value={createdToDate}
            onChange={(event) => onCreatedToDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[110px] flex-[0_1_120px]">
          <label htmlFor="admin-issues-filter-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Rows
          </label>
          <select
            id="admin-issues-filter-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
      </div>
    </AdminTableFiltersCard>
  );
};

export default AdminIssuesFilters;
